import React, { useState, useEffect, useRef } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

interface PensionData {
  [freelancerId: string]: {
    pension: boolean;
    pensionAmount: number;
  };
}

interface HolidayData {
  [freelancerId: string]: {
    holidaySavings: boolean;
    holidaySavingsFee: number;
  };
}

interface PensionHolidaySectionProps {
  freelancerId: string;
  freelancerName?: string;
  grossSalary: number;
  isRegularEngagement: boolean;
  globalData?: any;
  t: (key: string) => string;
  pensionData: PensionData;
  holidayData: HolidayData;
  onPensionChange: (data: PensionData) => void;
  onHolidayChange: (data: HolidayData) => void;
  calculatedAmount?: number;
}

const PensionHolidaySection: React.FC<PensionHolidaySectionProps> = ({
  freelancerId,
  freelancerName,
  grossSalary,
  isRegularEngagement,
  globalData,
  t: propT,
  pensionData,
  holidayData,
  onPensionChange,
  onHolidayChange,
  calculatedAmount
}) => {
  // Use translation hook
  const { t } = useTranslation();
  
  const pensionOptions = [1000, 1500, 2000, 2500, 3000];
  
  const [showErrorTooltip, setShowErrorTooltip] = useState(false);
  const [pensionHasBeenSelected, setPensionHasBeenSelected] = useState(false);
  const errorTooltipTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Get current values from state
  const currentPension = pensionData[freelancerId] || {
    pension: false,
    pensionAmount: pensionOptions[0]
  };

  const currentHoliday = holidayData[freelancerId] || {
    holidaySavings: false,
    holidaySavingsFee: globalData?.holidaySavingsFee?.[0]?.globalValue || 12
  };

  // Check if calculated amount meets pension requirement (5000 SEK minimum)
  const isAmountValid = Number(calculatedAmount || 0) >= 5000.00;

  // Auto-disable pension and show error when calculated amount drops below 5000
  useEffect(() => {
    // If pension is enabled and amount drops below 5000, disable pension
   if (pensionHasBeenSelected && !isAmountValid && calculatedAmount !== undefined) {
      // Clear any existing timer
      if (errorTooltipTimerRef.current) {
        clearTimeout(errorTooltipTimerRef.current);
      }

      // Show error tooltip
      setShowErrorTooltip(true);

      // Auto-disable pension and reset amount
      onPensionChange({
        ...pensionData,
        [freelancerId]: {
          pension: false,
          pensionAmount: 1000 // Set default to 1000
        }
      });

      // Set timer to hide tooltip after 5 seconds
      errorTooltipTimerRef.current = setTimeout(() => {
        setShowErrorTooltip(false);
      }, 5000);
    }
  }, [isAmountValid, calculatedAmount, currentPension.pension, freelancerId, onPensionChange, pensionData]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (errorTooltipTimerRef.current) {
        clearTimeout(errorTooltipTimerRef.current);
      }
    };
  }, []);

  const handlePensionCheckboxChange = (checked: boolean) => {
    if (checked) {
      setPensionHasBeenSelected(true);

      // Show error if trying to enable with insufficient calculated amount
      if (!isAmountValid) {
        if (errorTooltipTimerRef.current) {
          clearTimeout(errorTooltipTimerRef.current);
        }
        setShowErrorTooltip(true);
        errorTooltipTimerRef.current = setTimeout(() => {
          setShowErrorTooltip(false);
        }, 5000);
        // Don't enable pension if amount is invalid
        return;
      }
    }

    // Update pension state - always default to 1000 (first option) if no amount is set
    const defaultAmount = currentPension.pensionAmount || pensionOptions[0]; // Default to 1000 if not set
    
    onPensionChange({
      ...pensionData,
      [freelancerId]: {
        ...currentPension,
        pension: checked,
        pensionAmount: checked ? defaultAmount : 0
      }
    });
  };

  const handlePensionAmountChange = (amount: number) => {
    setPensionHasBeenSelected(true);

    // Show error tooltip if calculated amount is insufficient
    if (!isAmountValid) {
      if (errorTooltipTimerRef.current) {
        clearTimeout(errorTooltipTimerRef.current);
      }
      setShowErrorTooltip(true);
      errorTooltipTimerRef.current = setTimeout(() => {
        setShowErrorTooltip(false);
      }, 5000);
    }

    onPensionChange({
      ...pensionData,
      [freelancerId]: {
        ...currentPension,
        pensionAmount: amount
      }
    });
  };

  const handleHolidayCheckboxChange = (checked: boolean) => {
    onHolidayChange({
      ...holidayData,
      [freelancerId]: {
        ...currentHoliday,
        holidaySavings: checked
      }
    });
  };

  const handleHolidayFeeChange = (fee: number) => {
    onHolidayChange({
      ...holidayData,
      [freelancerId]: {
        ...currentHoliday,
        holidaySavingsFee: fee
      }
    });
  };

  return (
    <>
      <style>{`
        .pension-holiday-section input[type="checkbox"].form-check-input {
          width: 20px;
          height: 20px;
          border: 2px solid #d1d5db;
          border-radius: 4px;
          background-color: transparent;
          cursor: pointer;
          position: relative;
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        
        .pension-holiday-section input[type="checkbox"].form-check-input:checked {
          background-color: transparent;
          border-color: #6750b8;
        }
        
        .pension-holiday-section input[type="checkbox"].form-check-input:checked::after {
          content: '✓';
          color: #6750b8;
          font-size: 16px;
          font-weight: bold;
          line-height: 1;
          margin-top: -2px;
        }
        
        .pension-holiday-section input[type="checkbox"].form-check-input:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
      <div className="pension-holiday-section">
        <div className="col-custom d-block align-items-center justify-content-between pr-0">
          {/* Pension Section */}
          <OverlayTrigger
            show={showErrorTooltip && !isAmountValid && pensionHasBeenSelected}
            trigger={[]}
            placement="top"
            overlay={
              <Popover
                id={`pension-error-popover-${freelancerId}`}
                style={{ margin: 0 }}
              >
                <Popover.Body
                  style={{
                    backgroundColor: '#dc3545',
                    color: 'white',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)',
                    border: 'none'
                  }}
                >
                  {t('PENSION_ERROR_GROSS_SALARY')}
                </Popover.Body>
              </Popover>
            }
          >
          <div>
            <div className="form-check d-flex align-items-center mr-2">
              <input
                className="form-check-input text-dark cursor-pointer"
                type="checkbox"
                id={`pension-${freelancerId}`}
                disabled={!isRegularEngagement}
                checked={isRegularEngagement && currentPension.pension}
                onChange={(e) => handlePensionCheckboxChange(e.target.checked)}
              />
              <label
                htmlFor={`pension-${freelancerId}`}
                className="form-check-label pl-2 mt-1 cursor-pointer"
              >
                <p style={{
                  color: '#252B37',
                  fontFamily: '"Plus Jakarta Sans"',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  lineHeight: '140%',
                  margin: 0,
                  display: 'inline'
                }}>
                  {t('PENSION')}
                </p>
                {freelancerName && (
                  <span className="ml-2"  style={{
                  color: '#252B37',
                  fontFamily: '"Plus Jakarta Sans"',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  lineHeight: '140%',
                  margin: 0,
                  display: 'inline'
                }}>({freelancerName})</span>
                )}
              </label>
            </div>
            <div>
              <span
                className="my-2"
                style={{ 
                  whiteSpace: "break-spaces", 
                  color: '#252B37',
                  fontFamily: '"Plus Jakarta Sans"',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 300,
                  lineHeight: '140%',
                  letterSpacing: '0.07px'
                }}
              >
                {!isRegularEngagement
                  ? t('AVAILABLE_ONLY_REGULAR_ENGAGEMENT')
                  : t('PENSION_NOTE_5000_SEK')
                }
              </span>
            </div>
          </div>
        </OverlayTrigger>

        {/* Holiday Pay Section */}
        <div className="form-check d-flex align-items-center mr-2 mt-2">
          <input
            className="form-check-input text-dark cursor-pointer"
            type="checkbox"
            id={`holidayPay-${freelancerId}`}
            disabled={!isRegularEngagement}
            checked={isRegularEngagement && currentHoliday.holidaySavings}
            onChange={(e) => handleHolidayCheckboxChange(e.target.checked)}
          />
          <label
            className="form-check-label pl-2 mt-1 cursor-pointer"
            htmlFor={`holidayPay-${freelancerId}`}
          >
           <p style={{
                  color: '#252B37',
                  fontFamily: '"Plus Jakarta Sans"',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  lineHeight: '140%',
                  margin: 0,
                  display: 'inline'
                }}>{t('HOLIDAY_PAY')}</p>
            {freelancerName && (
              <span className="ml-2" style={{
                  color: '#252B37',
                  fontFamily: '"Plus Jakarta Sans"',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  lineHeight: '140%',
                  margin: 0,
                  display: 'inline'
                }}>({freelancerName})</span>
            )}
          </label>
        </div>
        <div>
          <span
            className="my-2"
           style={{ 
                  whiteSpace: "break-spaces", 
                  color: '#252B37',
                  fontFamily: '"Plus Jakarta Sans"',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 300,
                  lineHeight: '140%',
                  letterSpacing: '0.07px'
                }}
          >
            {!isRegularEngagement
              ? t('AVAILABLE_ONLY_REGULAR_ENGAGEMENT')
              : t('HOLIDAY_PAY_NOTE_190_SEK')
            }
          </span>
        </div>
      </div>
    </div>
    </>
  );
};

export default PensionHolidaySection;
